import React, { useState, useEffect, useContext } from "react";
import {  Button, Textarea } from "@nextui-org/react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import featuredImg from "../../Assets/blog/featured.svg";
import navShawarImg from "../../Assets/blog/Nev.png";
import likeIcon from "../../Assets/blog/like.svg";
import unlikeIcon from "../../Assets/blog/unlike.svg";
import commentLikeIcon from "../../Assets/blog/red_heart.png";
import commentUnlikeIcon from "../../Assets/blog/comment-unlike.svg";
import shareIcon from "../../Assets/blog/share.svg";
import arrowBack from "../../Assets/blog/arrow-back.svg";
import dots from "../../Assets/community/dots-vertical.svg";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
} from "@nextui-org/react";
import { Controller, useForm } from "react-hook-form";
import { PopUpContext } from "Context/PopUpContext";
import {  AuthContext } from "../../Context/AuthContext";
import ReactMarkdown from 'react-markdown';


// Blog Service - Centralized API calls using the working API
const blogService = {
  // Using slug-based API endpoints
  getAllBlogs: (token) => axios.get(`/blogs`, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  
  getBlogBySlug: (slug, token) => axios.get(`/blogs/${slug}`, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  
  // Get all comments for a blog (public endpoint)
  getBlogComments: (slug) => axios.get(`/blogs/${slug}/comments`),
  
  // Get all likes for a blog (public endpoint)
  getBlogLikes: (slug) => axios.get(`/blogs/${slug}/likes`),
  
  // Comment on blog using slug
  commentOnBlog: (slug, comment, token) => axios.post(
    `/blogs/${slug}/comment`,
    { comment },
    { headers: { Authorization: `Bearer ${token}` } }
  ),
  
  // Reply to a comment
  replyToComment: (slug, commentId, replyText, token) => axios.post(
    `/blogs/${slug}/comment/${commentId}/reply`,
    { comment: replyText },
    { headers: { Authorization: `Bearer ${token}` } }
  ),
  
  // Like/Unlike blog using slug
  likeBlog: (slug, token) => axios.post(
    `/blogs/${slug}/like`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  ),
  
  likeComment: (commentId, token) => axios.post(
    `/comments/${commentId}/toggle-like`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  ),
  
  // Delete a comment using slug
  deleteComment: (slug, commentId, token) => axios.delete(
    `/blogs/${slug}/comment/${commentId}`,
    { headers: { Authorization: `Bearer ${token}` } }
  ),
  
  // Edit a comment using slug
  editComment: (slug, commentId, commentText, token) => axios.put(
    `/blogs/${slug}/comment/${commentId}`,
    { comment: commentText },
    { headers: { Authorization: `Bearer ${token}` } }
  ),
  
// === ADMIN BLOG MANAGEMENT ===
  
  // Create a new blog post (Admin only)
  createBlog: (blogData, token) => axios.post(
    `/admin/blogs`,
    blogData,
    { headers: { Authorization: `Bearer ${token}` } }
  ),
  
  // Update an existing blog post (Admin only)
   updateBlog: (id, blogData, token) => axios.put(
    `/admin/blogs/${id}`,
    blogData,
    { headers: { Authorization: `Bearer ${token}` } }
  ),
  
  // Delete a blog post (Admin only)
  deleteBlog: (id, token) => axios.delete(
    `/admin/blogs/${id}`,
    { headers: { Authorization: `Bearer ${token}` } }
  ),
  
  
  // Publish a blog post (Admin only) - using the new publish endpoint
  publishBlog: (id, token) => axios.post(
    `/admin/blogs/${id}/publish`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  ),
  unpublishBlog: (id, token) => axios.post(
    `/admin/blogs/${id}/unpublish`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  ),
  
  
  // Upload blog image (Admin only)
   uploadImage: (formData, token) => axios.post(
    `/admin/blogs/upload-image`,
    formData,
    { 
      headers: { 
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data"
      } 
    }
  ),
};

// Helper function to check if user is admin
const isAdminUser = (user) => {
  if (!user) {
    //console.log('❌ isAdminUser: No user object provided');
    return false;
  }
  
  //console.log('👤 isAdminUser checking user:', user);
  
  // Check for role-based admin access
  const userRole = user?.role || user?.user_type || user?.is_admin;
  //console.log('🎯 isAdminUser - user role:', userRole);
  
  // Hardcoded admin IDs for fallback
  const adminIds = [4, 83];
  const isHardcodedAdmin = adminIds.includes(parseInt(user?.id));
  //console.log('🔢 isAdminUser - is hardcoded admin:', isHardcodedAdmin, '(ID:', user?.id, ')');
  
  // Check if user has admin role (case insensitive)
  const isRoleAdmin = userRole === 'admin' || userRole === 'Admin' || userRole === 1 || userRole === true;
  //console.log('👑 isAdminUser - is role admin:', isRoleAdmin, '(role:', userRole, ')');
  
  const result = isHardcodedAdmin || isRoleAdmin;
  //console.log('✅ isAdminUser - final result:', result);
  
  return result;
};

// Time ago helper function
const timeAgo = (dateString) => {
  const now = new Date();
  const date = new Date(dateString);
  const diffInSeconds = Math.floor((now - date) / 1000);

  const timeUnits = [
    { name: "year", seconds: 31536000 },
    { name: "month", seconds: 2592000 },
    { name: "day", seconds: 86400 },
    { name: "hour", seconds: 3600 },
    { name: "minute", seconds: 60 },
    { name: "second", seconds: 1 },
  ];

  for (const unit of timeUnits) {
    const interval = Math.floor(diffInSeconds / unit.seconds);
    if (interval >= 1) {
      return `${interval} ${unit.name}${interval > 1 ? "s" : ""} ago`;
    }
  }

  return "just now";
};

// Helper function to convert HTML to Tailwind classes
const formatBlogContent = (html) => {
  if (!html) return null;
  
  return (
    <div className="space-y-6">
      {/* You'll need to parse the HTML and convert it manually */}
      {html.split('\n').map((line, index) => {
        if (line.startsWith('<h1>')) {
          return <h1 key={index} className="mt-8 mb-4 text-4xl font-bold">{line.replace(/<[^>]+>/g, '')}</h1>;
        }
        if (line.startsWith('<h2>')) {
          return <h2 key={index} className="mt-6 mb-3 text-3xl font-bold">{line.replace(/<[^>]+>/g, '')}</h2>;
        }
        if (line.startsWith('<p>')) {
          return <p key={index} className="mb-4 text-lg leading-relaxed">{line.replace(/<[^>]+>/g, '')}</p>;
        }
        if (line.startsWith('<ul>')) {
          return <ul key={index} className="pl-6 mb-4 list-disc">{line.replace(/<[^>]+>/g, '')}</ul>;
        }
        if (line.startsWith('<li>')) {
          return <li key={index} className="mb-2">{line.replace(/<[^>]+>/g, '')}</li>;
        }
        return <p key={index} className="text-lg leading-relaxed">{line}</p>;
      })}
    </div>
  );
};


// Format date for display
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

// Get category color
const getCategoryColor = (category) => {
  const colors = {
    "Travel Tips": "#4F46E5",
    "Travel": "#4F46E5",
    "Projects": "#10B981",
    "Event": "#F59E0B",
    "Productivity": "#EF4444",
    "Seasonal": "#8B5CF6",
    "Technology": "#EC4899",
    "Lifestyle": "#06B6D4"
  };
  return colors[category] || "#4F46E5";
};

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { setIsRefetchTrending } = useContext(PopUpContext);
  
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [commentsList, setCommentsList] = useState([]);
  const [likes, setLikes] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [shareModal, setShareModal] = useState(false);
  const [targetId, setTargetId] = useState(null);
  const [targetComment, setTargetComment] = useState(null);
  const [isEditCommentModalOpen, setIsEditCommentModalOpen] = useState(false);
  const [itemKey, setItemKey] = useState(null);
  const [showReplies, setShowReplies] = useState([]);
  const [commentId, setCommentId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [descLength, setDescLength] = useState(0);
  const [replyDescLength, setReplyDescLength] = useState(0);
  const [error, setError] = useState(null);
  // const { user, token } = useContext(AuthContext);
  const [post, setPost] = useState(null);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState("");
  const [editCommentLength, setEditCommentLength] = useState(0);

  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authorData, setAuthorData] = useState(null); // For Nav Shawer (ID: 4)
  
  const authToken = token;

  // Load user data and check admin status
  useEffect(() => {
    const loadUserData = () => {
      try {
        // Get token
        const storedToken = localStorage.getItem("token");
        setToken(storedToken);
        //console.log('🔐 Token loaded:', storedToken ? 'Token exists' : 'No token');
        
        // Get user data
        const userData = localStorage.getItem("user_data");
        if (userData) {
          const parsedUser = JSON.parse(userData);
          //console.log('👤 User data loaded:', parsedUser);
          setUser(parsedUser);
          
          // Check if admin using the improved function
          const adminStatus = isAdminUser(parsedUser);
          setIsAdmin(adminStatus);
          //console.log('👑 Admin status set:', adminStatus);
        } else {
          //console.log('❌ No user_data in localStorage');
          setUser(null);
          setIsAdmin(false);
        }
      } catch (error) {
        console.error("Error loading user data:", error);
        setUser(null);
        setIsAdmin(false);
      }
    };
    
    loadUserData();
  }, []);

  // Fetch Nav Shawer (author with ID 4) data
  // useEffect(() => {
  //   const fetchAuthorData = async () => {
  //     try {
  //       const response = await axios.get(
  //         `/users/4`,
  //         {
  //           headers: {
  //             Accept: "application/json",
  //             Authorization: `Bearer ${authToken}`,
  //           },
  //         }
  //       );
  //       //console.log('👤 Nav Shawer data:', response.data);
  //       setAuthorData(response.data);
  //     } catch (error) {
  //       console.error('Error fetching author data:', error);
  //     }
  //   };

  //   fetchAuthorData();
  // }, [authToken]);

// Add this debug useEffect to check the image data
useEffect(() => {
  if (post) {
    //console.log("=== POST IMAGE DEBUG ===");
    //console.log("Post object:", post);
    //console.log("Post.image:", post.image);
    //console.log("Post.featured_image:", post.image);
    //console.log("Full URL for testing:", post.image || post.featured_image);
    
    // Test if the URL is accessible
    if (post.image || post.featured_image) {
      const imgUrl = post.image || post.featured_image;
      //console.log("Image URL type:", typeof imgUrl);
      //console.log("Image URL starts with http:", imgUrl?.startsWith('http'));
      //console.log("Image URL is relative:", imgUrl?.startsWith('/'));
      
      // Test if image loads
      const testImg = new Image();
      testImg.onload = () => //console.log("✅ Image loads successfully");
      testImg.onerror = () => //console.log("❌ Image failed to load");
      testImg.src = imgUrl;
    }
  }
}, [post]);


  
useEffect(() => {
  //console.log("All localStorage keys:", Object.keys(localStorage));
  //console.log("LocalStorage contents:");
  Object.keys(localStorage).forEach(key => {
    //console.log(`${key}:`, localStorage.getItem(key));
  });
}, []);

  useEffect(() => {
  if (commentsList.length > 0) {
    //console.log('First comment data:', commentsList[0]);
    //console.log('Current user:', user);
  }
}, [commentsList, user]);

useEffect(() => {
  const loadUserData = async () => {
    try {
      // Get token
      const storedToken = localStorage.getItem("token");
      setToken(storedToken);
      
      if (!storedToken) {
        //console.log("No token found");
        setUser(null);
        return;
      }
      
      // Try to get user data from localStorage first
      const userData = localStorage.getItem("user_data");
      if (userData) {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        checkAdminStatus(parsedUser);
        return;
      }
      
      // If no user_data in localStorage, fetch it from API
      //console.log("Fetching user data from API...");
      const response = await axios.get(
        "/user",
        {
          headers: {
            Authorization: `Bearer ${storedToken}`,
            Accept: "application/json"
          }
        }
      );
      
      if (response.data) {
        const userData = response.data;
        setUser(userData);
        
        // Store in localStorage for future use
        localStorage.setItem("user_data", JSON.stringify(userData));
        
        // Check if admin
        checkAdminStatus(userData);
      }
      
    } catch (error) {
      console.error("Error loading user data:", error);
      setUser(null);
    }
  };
  
  const checkAdminStatus = (userData) => {
    const adminIds = [4, 83];
    if (userData?.id) {
      setIsAdmin(adminIds.includes(parseInt(userData.id)));
    }
  };
  
  loadUserData();
}, []);


// Add this debug to check localStorage
useEffect(() => {
  //console.log("LocalStorage contents:");
  //console.log("token:", localStorage.getItem("token"));
  //console.log("user_data:", localStorage.getItem("user_data"));
  //console.log("user_id:", localStorage.getItem("user_id"));
  
  // Check all localStorage keys
  //console.log("All localStorage keys:", Object.keys(localStorage));
  
  // If you're logged in, user should not be null
  //console.log("User state:", user);
}, [user]);

// useEffect(() => {
//     const checkIfAdmin = async () => {
//       if (token) {
//         setIsAdmin(false);
//         return;
//       }
      
//       try {
//         const response = await axios.post('/user', {
//           headers: { Authorization: `Bearer ${token}` }
//         });
        
//         const userId = response.data.id;
//         const adminIds = [4, 83];
//         //console.log('user id admin', userId)
//         setIsAdmin(adminIds.includes(userId));
//       } catch (error) {
//         setIsAdmin(false);
//       }
//     };
    
//     checkIfAdmin();
//   }, [token]);   



  // Comment form
  const {
    control: controlForm1,
    handleSubmit: handleSubmitForm1,
    reset: resetForm1,
    formState: { errors: errorsForm1 },
  } = useForm({
    defaultValues: {
      desc: "",
    },
  });

  // Reply form
  const {
    control: controlForm2,
    handleSubmit: handleSubmitForm2,
    reset: resetForm2,
    formState: { errors: errorsForm2 },
  } = useForm({
    defaultValues: {
      desc2: "",
    },
  });


const isMyComment = (comment) => {
  try {
    // Get user ID from localStorage directly
    const userData = localStorage.getItem("user_data");
    //console.log("isMyComment - userData from localStorage:", userData);
    
    if (!userData) {
      //console.log("isMyComment - No user_data in localStorage");
      return false;
    }
    
    const parsedUser = JSON.parse(userData);
    const currentUserId = parsedUser?.id;
    
    //console.log("isMyComment - currentUserId:", currentUserId);
    
    if (!currentUserId) {
      //console.log("isMyComment - No user ID found");
      return false;
    }
    
    // Check all possible user ID locations in comment
    const commentUserId = 
      comment.user?.id || 
      comment.user_id || 
      comment.user_data?.id;
    
    // console.log("isMyComment - commentUserId:", commentUserId);
    // console.log("isMyComment - comment object:", {
    //   user: comment.user,
    //   user_id: comment.user_id,
    //   user_data: comment.user_data
    // });
    
    const result = String(commentUserId) === String(currentUserId);
    // console.log("isMyComment - result:", result);
    
    return result;
  } catch (error) {
    console.error("isMyComment - Error:", error);
    return false;
  }
};


  // Fetch blog post by slug - using real API
  useEffect(() => {
    const fetchBlogPost = async () => {
      window.scrollTo(0, 0);
      setIsLoading(true);
      setError(null);
      
      try {
        // Fetch all blogs from API and find by slug
        const response = await axios.get(
          `/blogs`,
          {
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        
        const allBlogs = response.data.data || response.data || [];
        
        // Find post by slug
        const foundPost = Array.isArray(allBlogs) 
          ? allBlogs.find(p => p.slug === slug) 
          : null;
        
        if (foundPost) {
          // Transform API data to match component structure
          const transformedPost = {
            id: foundPost.id,
            slug: foundPost.slug,
            title: foundPost.title,
            excerpt: foundPost.excerpt,
            fullContent: foundPost.content || foundPost.fullContent || `<p>${foundPost.excerpt || ''}</p>`,
            category: foundPost.category,
            featured_image: foundPost.image,
            author: foundPost.user?.name || foundPost.author || "Admin",
            authorAvatar: (foundPost.user?.name || foundPost.author || "A").charAt(0),
            date: formatDate(foundPost.published_at || foundPost.created_at),
            image: foundPost.image ,
            is_published: foundPost.is_published,
            likes_count: foundPost.likes_count,
            comments_count: foundPost.comments_count
          };
          
          setPost(transformedPost);
          
          // Get related posts from the same category
          const related = Array.isArray(allBlogs)
            ? allBlogs.filter(p => p.category === foundPost.category && p.id !== foundPost.id).slice(0, 3)
            : [];
          setRelatedPosts(related);
          
          // Fetch comments and likes for this post using public API endpoints
          try {
// Fetch comments
            const commentsResponse = await blogService.getBlogComments(foundPost.slug);
            const commentsData = commentsResponse.data;
            
            // Debug: Log the API response to check like_count and is_liked fields
            //console.log('Comments API response:', commentsData);
            //console.log('First comment structure:', commentsData?.comments?.[0]);
            
            // Ensure comments have like_count and is_liked fields
            const normalizedComments = (commentsData?.comments || []).map(comment => ({
              ...comment,
              like_count: comment.like_count ?? comment.likes_count ?? 0,
              is_liked: comment.is_liked ?? false
            }));
            setCommentsList(normalizedComments);
            
            // Fetch likes
            const likesResponse = await blogService.getBlogLikes(foundPost.slug);
            const likesData = likesResponse.data;
            setLikes(parseInt(likesData?.likes_count) || foundPost.likes_count || 0);
            setIsLiked(likesData?.is_liked || false);
          } catch (apiError) {
            // Use empty data if API fails
            setCommentsList([]);
            setLikes(foundPost.likes_count || 0);
            setIsLiked(false);
          }
        } else {
          setError("Blog post not found");
        }
        
      } catch (error) {
        console.error("Error fetching blog post:", error);
        setError("Blog post not found");
        toast.error("Failed to load blog post");
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchBlogPost();
  }, [slug, authToken]);

  // Handle like using slug-based API
  const handleLike = async () => {
    try {
      if (!authToken) {
        toast.info("Please login to like blogs");
        return;
      }
      
      if (!post?.slug) {
        toast.error("Invalid blog post");
        return;
      }
      
      // Using slug-based API endpoint
      const { data } = await blogService.likeBlog(post.slug, authToken);
      setLikes(data.likes_count);
      setIsLiked(!isLiked); // Toggle based on current state (API doesn't return is_liked)
      toast.success(data.message || "Blog liked");
    } catch (error) {
      // Fallback to local state toggle on error
      setLikes(isLiked ? Math.max(0, likes - 1) : likes + 1);
      setIsLiked(!isLiked);
      toast.error(error?.response?.data?.message || "Failed to like blog");
    }
  };

  // Handle share
  const handleShare = () => {
    if (!post) return;
    
    const shareData = {
      title: post.title,
      text: post.excerpt,
      url: window.location.href
    };

    if (navigator.share) {
      navigator.share(shareData).catch(console.error);
    } else {
      navigator.clipboard.writeText(window.location.href).then(() => {
        setShareModal(true);
        setTimeout(() => setShareModal(false), 2000);
      });
    }
  };

// Admin functions - using new Admin API endpoints
  const handleEditPost = () => {
    if (!post || !isAdmin) return;
    navigate(`/admin/blog/edit/${post.id}`);
  };

const handleDeletePost = async () => {
  //console.log('🗑️ handleDeletePost called');
  //console.log('📋 Post data:', post);
  //console.log('👤 isAdmin:', isAdmin);
  //console.log('🔐 authToken:', authToken ? 'Token exists' : 'NO TOKEN');
  
  if (!post || !isAdmin) {
    //console.log('❌ Delete blocked: !post || !isAdmin');
    toast.error("You are not authorized to delete this post");
    return;
  }
  
  if (!authToken) {
    //console.log('❌ Delete blocked: No auth token');
    toast.error("Please login to delete posts");
    return;
  }
  
  if (!window.confirm(`Are you sure you want to delete "${post.title}"? This action cannot be undone.`)) {
    //console.log('❌ Delete cancelled by user');
    return;
  }
  
  try {
    // Show loading
    setIsLoading(true);
    //console.log('🚀 Starting delete request for post ID:', post.id);
    
    // Using the correct delete endpoint
    const response = await blogService.deleteBlog(post.id, authToken);
    //console.log('✅ Delete response:', response.data);
    
    toast.success(response.data.message || 'Blog post deleted successfully');
    
    // Navigate back to blog list
    navigate('/blogs');
    
  } catch (error) {
    console.error('❌ Delete error:', error);
    console.error('❌ Error response:', error.response?.data);
    console.error('❌ Error status:', error.response?.status);
    
    if (error.response?.status === 404) {
      toast.error('Blog post not found or already deleted');
    } else if (error.response?.status === 403) {
      toast.error('You are not authorized to delete this blog post');
    } else if (error.response?.status === 401) {
      toast.error('Session expired. Please login again.');
    } else {
      toast.error(error?.response?.data?.message || 'Failed to delete blog post');
    }
    
    setIsLoading(false);
  }
};




const handlePublishToggle = async () => {
  if (!post || !isAdmin) return;
  
  try {
    if (post.is_published) {
      // Unpublish the blog post
      const response = await blogService.unpublishBlog(post.id, authToken);
      toast.success(response.data.message || 'Blog unpublished successfully');
      
      // Update local state
      if (response.data.blog) {
        setPost({
          ...post,
          is_published: response.data.blog.is_published,
          published_at: response.data.blog.published_at
        });
      } else {
        setPost({ ...post, is_published: false });
      }
    } else {
      // Publish the blog post
      const response = await blogService.publishBlog(post.id, authToken);
      toast.success(response.data.message || 'Blog published successfully');
      
      // Update local state
      if (response.data.blog) {
        setPost({
          ...post,
          is_published: response.data.blog.is_published,
          published_at: response.data.blog.published_at
        });
      } else {
        setPost({ ...post, is_published: true });
      }
    }
  } catch (error) {
    console.error('Publish/Unpublish error:', error);
    toast.error(error?.response?.data?.message || 'Failed to update publication status');
  }
};




  // Comment function using slug-based API
  const commentPost = async (reqBody) => {
    try {
      if (!authToken) {
        toast.info("Please login to comment");
        return;
      }
      
      if (!post?.slug) {
        toast.error("Invalid blog post");
        return;
      }
      
      // Using slug-based API endpoint
      await blogService.commentOnBlog(post.slug, reqBody.desc, authToken);
      resetForm1();
      setDescLength(0);
      toast.success('Comment added');
      
      // Refresh comments to show the new one
      try {
        const commentsResponse = await axios.get(
          `/blogs/${post.slug}`,
          {
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        const postData = commentsResponse.data.data || commentsResponse.data;
        setCommentsList(postData?.comments || []);
      } catch (refreshError) {
        console.error("Error refreshing comments:", refreshError);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to add comment");
    }
  };

  // Add state to track liked comments
  const [likedComments, setLikedComments] = useState({});

  const likeComment = async (id) => {
    try {
      if (!authToken) {
        toast.info("Please login to like comments");
        return;
      }
      
      // Toggle local state immediately for optimistic UI
      const isCurrentlyLiked = likedComments[id] || false;
      const newLikedState = !isCurrentlyLiked;
      
      // Update local state optimistically
      setLikedComments(prev => ({
        ...prev,
        [id]: newLikedState
      }));
      
      // Update comments list with new like count and liked state
      setCommentsList(prevComments => 
        prevComments.map(comment => {
          if (comment.id === id) {
            return {
              ...comment,
              is_liked: newLikedState,
              like_count: newLikedState 
                ? (comment.like_count || 0) + 1 
                : Math.max(0, (comment.like_count || 0) - 1)
            };
          }
          // Also check replies
          if (comment.replies) {
            return {
              ...comment,
              replies: comment.replies.map(reply => {
                if (reply.id === id) {
                  return {
                    ...reply,
                    is_liked: newLikedState,
                    like_count: newLikedState 
                      ? (reply.like_count || 0) + 1 
                      : Math.max(0, (reply.like_count || 0) - 1)
                  };
                }
                return reply;
              })
            };
          }
          return comment;
        })
      );
      
      // Call API
      await blogService.likeComment(id, authToken);
      
      // Only show toast on success, don't override the optimistic update
      // toast.success(newLikedState ? 'Liked comment' : 'Unliked comment');
    } catch (error) {
      // Revert on error
      setLikedComments(prev => ({
        ...prev,
        [id]: !prev[id]
      }));
      
      toast.error(error?.response?.data?.message || "Failed to like comment");
    }
  };

  // Add state to track deleting comments and prevent duplicate requests
  const [deletingCommentId, setDeletingCommentId] = useState(null);

  const deleteComment = async (commentId = targetId, isReplyDelete = false, parentCommentId = null) => {
    try {
      if (!authToken) {
        toast.info("Please login to delete comments");
        return { success: false, error: "Not authenticated" };
      }
      
      if (!post?.slug) {
        toast.error("Invalid blog post");
        return { success: false, error: "Invalid post" };
      }
      
      const commentIdToDelete = commentId || targetId;
      
      if (!commentIdToDelete) {
        toast.error("Invalid comment ID");
        return { success: false, error: "Invalid comment ID" };
      }

      // Prevent duplicate delete requests
      if (deletingCommentId === commentIdToDelete) {
        //console.log('Delete already in progress for comment:', commentIdToDelete);
        return { success: false, error: "Delete in progress" };
      }

      // Set deleting state
      setDeletingCommentId(commentIdToDelete);
      //console.log('Attempting to delete comment:', commentIdToDelete);
      //console.log('Current comments list IDs:', commentsList.map(c => c.id));
      
      // Verify comment exists in current state before attempting delete
      const commentExistsInState = commentsList.some(c => c.id === commentIdToDelete) ||
        commentsList.some(c => c.replies?.some(r => r.id === commentIdToDelete));
      
      if (!commentExistsInState) {
        console.warn('Comment not found in state, may have already been deleted:', commentIdToDelete);
        // Still try the API call as it might exist on backend
      }

      // Using slug-based API endpoint
      await blogService.deleteComment(post.slug, commentIdToDelete, authToken);
      
      toast.success('Comment deleted');
      //console.log('Comment deleted successfully:', commentIdToDelete);
      
      // Update state only after successful API call
      if (isReplyDelete && parentCommentId) {
        // Update replies in parent comment
        setCommentsList(commentsList.map(c => {
          if (c.id === parentCommentId) {
            return {
              ...c,
              replies: c.replies?.filter(r => r.id !== commentIdToDelete) || []
            };
          }
          return c;
        }));
      } else {
        // Remove top-level comment
        setCommentsList(commentsList.filter(c => c.id !== commentIdToDelete));
      }
      
      setTargetId(null);
      setDeletingCommentId(null);
      
      return { success: true };
    } catch (error) {
      console.error('Error deleting comment:', error);
      console.error('Error response:', error.response?.data);
      
      // Check if it's a "not found" error (comment already deleted or invalid ID)
      if (error.response?.status === 404 || 
          error.response?.data?.message?.includes('No query results') ||
          error.response?.data?.message?.includes('Model not found')) {
        
        toast.info("Comment was already deleted or doesn't exist");
        
        // Remove from UI anyway to sync with backend
        if (isReplyDelete && parentCommentId) {
          setCommentsList(commentsList.map(c => {
            if (c.id === parentCommentId) {
              return {
                ...c,
                replies: c.replies?.filter(r => r.id !== (commentId || targetId)) || []
              };
            }
            return c;
          }));
        } else {
          setCommentsList(commentsList.filter(c => c.id !== (commentId || targetId)));
        }
        
        setTargetId(null);
        setDeletingCommentId(null);
        return { success: true };
      }
      
      toast.error(error?.response?.data?.message || "Failed to delete comment");
      setDeletingCommentId(null);
      return { success: false, error: error.response?.data?.message || "Delete failed" };
    }
  };

  const editComment = async () => {
    try {
      if (!authToken) {
        toast.info("Please login to edit comments");
        return;
      }
      
      if (!post?.slug || !editingCommentId) {
        toast.error("Invalid comment");
        return;
      }
      
      if (editingCommentText.trim().length < 2) {
        toast.error("Comment must be at least 2 characters");
        return;
      }
      
      // Using slug-based API endpoint
      await blogService.editComment(post.slug, editingCommentId, editingCommentText, authToken);
      
      toast.success('Comment updated');
      
      // Update the comment in the list - handle both top-level comments and replies
      setCommentsList(prevComments => 
        prevComments.map(comment => {
          // Check if editing a top-level comment
          if (comment.id === editingCommentId) {
            return { ...comment, comment: editingCommentText, updated_at: new Date().toISOString() };
          }
          // Check if editing a reply
          if (comment.replies && comment.replies.length > 0) {
            return {
              ...comment,
              replies: comment.replies.map(reply => 
                reply.id === editingCommentId
                  ? { ...reply, comment: editingCommentText, updated_at: new Date().toISOString() }
                  : reply
              )
            };
          }
          return comment;
        })
      );
      
      setEditingCommentId(null);
      setEditingCommentText("");
      setEditCommentLength(0);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to edit comment");
    }
  };

  const cancelEditComment = () => {
    setEditingCommentId(null);
    setEditingCommentText("");
    setEditCommentLength(0);
  };

  const startEditComment = (comment) => {
    setEditingCommentId(comment.id);
    setEditingCommentText(comment.comment);
    setEditCommentLength(comment.comment.length);
  };

  const replyComment = async (reqBody) => {
    try {
      if (!authToken) {
        toast.info("Please login to reply");
        return;
      }
      
      if (!post?.slug) {
        toast.error("Invalid blog post");
        return;
      }
      
      // Using slug-based API endpoint
      await blogService.replyToComment(post.slug, commentId, reqBody.desc2, authToken);
      
      resetForm2();
      setReplyDescLength(0);
      setCommentId(null);
      toast.success('Reply added');
      
      // Refresh comments to show the new reply
      try {
        const commentsResponse = await blogService.getBlogComments(post.slug);
        const commentsData = commentsResponse.data;
        setCommentsList(commentsData?.comments || []);
      } catch (refreshError) {
        console.error("Error refreshing comments:", refreshError);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to add reply");
    }
  };

  const toggleReplies = (id) => {
    setShowReplies(
      (prev) =>
        prev.includes(id)
          ? prev.filter((itemId) => itemId !== id)
          : [...prev, id]
    );
  };

  const openModal = (key, id, comment) => {
    setItemKey(key);
    if (id) setTargetId(id);
    if (comment) setTargetComment(comment);
  };

  // Handle itemKey changes
  useEffect(() => {
    if (itemKey === "edit comment") {
      setIsEditCommentModalOpen(true);
      setItemKey(null);
    } else if (itemKey === "delete comment") {
      if (targetId) {
        deleteComment(targetId, false, null);
      }
      setTargetId(null);
      setItemKey(null);
    }
  }, [itemKey, targetId]);

  if (isLoading) {
    return (
      <section className="py-16 blog-post">
        <div className="container px-6 mx-auto text-center xl:px-20">
          <div className="w-16 h-16 mx-auto border-t-2 border-b-2 rounded-full animate-spin border-mainGradiant"></div>
          <p className="mt-4 text-gray-600">Loading Events...</p>
        </div>
      </section>
    );
  }

  if (error || !post) {
    return (
      <section className="py-16 blog-post">
        <div className="container px-6 mx-auto text-center xl:px-20">
          <h2 className="text-2xl font-bold text-gray-700">{error || "Post not found"}</h2>
          <Button 
            className="mt-4 text-white bg-mainGradiant"
            onClick={() => navigate("/blogs")}
          >
            Back to Events
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section className="relative blog-post">
      {/* Hero Section */}
      <div className="py-12 intro bg-mainGradiant">
        <div className="container px-6 mx-auto xl:px-20 md:px-3">
          <Button
            className="mb-6 text-white bg-white/20"
            onClick={() => navigate("/blogs")}
          >
            <img src={arrowBack} alt="Back" className="w-4 h-4 mr-2" />
            Back to Events
          </Button>
        </div>
      </div>

      {/* Article Content */}
      <div className="relative article-content py-14">
        {/* Decorative Bubbles */}
        <div className="absolute left-0 top-0 sm:w-[367px] w-[80%] h-[362px] z-10 bg-aboutBuble"></div>
        <div className="absolute left-0 bottom-[0] sm:w-[344px] w-[80%] h-[384.17px] z-10 bg-aboutOrangeBuble"></div>
        <div className="absolute right-0 bottom-[15%] sm:w-[260px] w-[50%] h-[348px] z-10 bg-aboutPrint"></div>

        <div className="container relative z-20 px-6 mx-auto xl:px-20">
          {/* Article Header */}
          <div className="max-w-4xl mx-auto mb-10">
            <div className="flex flex-wrap items-center gap-4 mb-6">
              {post.category && (
                <span 
                  className="px-4 py-1 text-sm font-semibold text-white rounded-full"
                  style={{ backgroundColor: getCategoryColor(post.category) }}
                >
                  {post.category}
                </span>
              )}
              <span className="text-gray-500">{post.date}</span>
              {post.is_published ? (
                <span className="px-3 py-1 text-sm font-semibold text-green-800 bg-green-100 rounded-full">
                  Published
                </span>
              ) : (
                <span className="px-3 py-1 text-sm font-semibold text-yellow-800 bg-yellow-100 rounded-full">
                  Draft
                </span>
              )}
            </div>
            
            <h1 className="mb-6 text-4xl font-bold leading-tight text-gray-900 md:text-5xl">
              {post.title}
            </h1>
            
            {post.excerpt && (
              <p className="mb-8 text-xl leading-relaxed text-gray-600">
                {post.excerpt}
              </p>
            )}

            {/* Author Info */}
            <div className="flex items-center justify-between pb-8 border-b border-gray-200">
              <div className="flex items-center gap-4">
                <div className="flex items-center justify-center w-12 h-12 overflow-hidden rounded-full bg-mainGradiant">
                  {authorData?.img_url ? (
                    <img 
                      src={authorData.img_url} 
                      alt={authorData.name || "Nav Shawer"}
                      className="object-cover w-full h-full"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = `<img src="${navShawarImg}" alt="Nav Shawer" class="w-full h-full object-cover" />`;
                      }}
                    />
                  ) : (
                    <img 
                      src={navShawarImg} 
                      alt="Nav Shawer" 
                      className="object-cover w-full h-full"
                    />
                  )}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">
                    {authorData?.name || "Nav Shawer"}
                  </p>
                  <p className="text-sm text-gray-500">Author</p>
                </div>
              </div>
              
              {/* Admin Actions */}
              {isAdmin && (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    color="warning"
                    onClick={() => navigate("/admin/blog")}
                    className="text-white bg-gradient-to-r from-pink-500 to-orange-500"
                    startContent={<i className="fas fa-blog"></i>}
                  >
                    Manage Events
                  </Button>
                  <Button
                    size="sm"
                    color="warning"
                    onClick={handleEditPost}
                    className="text-white bg-gradient-to-r from-yellow-500 to-orange-500"
                    startContent={<i className="fas fa-edit"></i>}
                  >
                    Edit
                  </Button>
                  
                  <Button
                    size="sm"
                    color={post.is_published ? "default" : "success"}
                    onClick={handlePublishToggle}
                    className={
                      post.is_published 
                        ? "bg-gradient-to-r from-gray-500 to-gray-600 text-white" 
                        : "bg-gradient-to-r from-green-500 to-emerald-500 text-white"
                    }
                    startContent={
                      post.is_published 
                        ? <i className="fas fa-eye-slash"></i>
                        : <i className="fas fa-eye"></i>
                    }
                  >
                    {post.is_published ? "Unpublish" : "Publish"}
                  </Button>
                  
                  <Button
                    size="sm"
                    color="danger"
                    onClick={handleDeletePost}
                    className="text-white bg-gradient-to-r from-red-500 to-rose-500"
                    startContent={<i className="fas fa-trash"></i>}
                    isLoading={isLoading}
                  >
                    Delete
                  </Button>
                </div>
              )}
                          </div>
          </div>

          {/* Featured Image */}
       {/* Featured Image */}
          {(post.image) && (
            <div className="max-w-5xl mx-auto mb-10">
              <div className="overflow-hidden border-2 border-solid rounded-3xl border-cardColor">
                <img
                    src={post.image || post.featured_image}
                    alt={post.title}
                    className="w-full h-[400px] object-cover"
                    onError={(e) => {
                      console.error("Image failed to load:", e.target.src);
                      e.target.src = featuredImg;
                    }}
                  />
              </div>
            </div>
          )}
          {/* Article Body */}
          <div className="max-w-3xl mx-auto mb-16">
            {post.fullContent ? (
              <div 
                className="prose prose-lg prose-blue max-w-none prose-a:no-underline prose-a:text-blue-600 hover:prose-a:text-blue-800"
                style={{ textAlign: 'justify' }}
                dangerouslySetInnerHTML={{ __html: post.fullContent }}
              />
            ) : (
    <div className="py-12 text-center">
      <p className="text-gray-500">No content available for this post.</p>
    </div>
  )}
</div>

          {/* Like and Share */}
          <div className="max-w-3xl mx-auto mb-16">
            <div className="flex items-center justify-center gap-6">
              <button
                onClick={handleLike}
                className={`flex items-center gap-3 px-6 py-3 rounded-full transition-all duration-300 ${
                  isLiked 
                    ? "bg-[#FDF2F8] text-[#E65CA7]" 
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                <div className="w-6 h-6">
                  <img
                    src={isLiked ? commentLikeIcon : unlikeIcon}
                    alt="Like"
                    className="w-full h-full"
                  />
                </div>
                <span className="font-medium">{likes}</span>
              </button>

              <button
                onClick={handleShare}
                className="flex items-center gap-3 px-6 py-3 text-gray-600 transition-all duration-300 bg-gray-100 rounded-full hover:bg-gray-200"
              >
                <div className="w-6 h-6">
                  <img
                    src={shareIcon}
                    alt="Share"
                    className="w-full h-full"
                  />
                </div>
                <span className="font-medium">Share</span>
              </button>
            </div>
          </div>

          {/* Related Posts */}
          {relatedPosts.length > 0 && (
            <div className="mb-16 related-posts">
              <h3 className="text-38px font-bold leading-[43.7px] bg-clip-text text-transparent bg-mainGradiant mb-8 text-center">
                Related Articles
              </h3>
              <div className="grid gap-8 md:grid-cols-3">
                {relatedPosts.map((relatedPost) => (
                  <div
                    key={relatedPost.id}
                    className="overflow-hidden transition-shadow duration-300 bg-white border-2 border-solid cursor-pointer rounded-3xl border-cardColor hover:shadow-xl"
                    onClick={() => navigate(`/blogs/${relatedPost.slug}`)}
                  >
                   {relatedPost.image && (
                    <div className="relative h-48">
                      <img
                        src={relatedPost.image}
                        alt={relatedPost.title}
                        className="object-cover w-full h-full"
                        onError={(e) => {
                          console.error("Related image failed to load:", e.target.src);
                          e.target.src = featuredImg; // Fallback image
                        }}
                      />
                        {relatedPost.category && (
                          <span 
                            className="absolute px-3 py-1 text-xs font-semibold text-white rounded-full top-4 left-4"
                            style={{ backgroundColor: getCategoryColor(relatedPost.category) }}
                          >
                            {relatedPost.category}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="p-6">
                      <h4 className="mb-2 text-lg font-bold text-gray-900 line-clamp-2">
                        {relatedPost.title}
                      </h4>
                      {relatedPost.excerpt && (
                        <p className="mb-4 text-sm text-gray-600 line-clamp-3">
                          {relatedPost.excerpt}
                        </p>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">
                          {formatDate(relatedPost.published_at || relatedPost.created_at)}
                        </span>
                        <span className="text-sm font-medium text-mainGradiant">Read More →</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Comments Section */}
          <div className="max-w-3xl mx-auto">
            <h3 className="mb-8 text-2xl font-bold text-gray-900">
              Comments ({commentsList?.length || 0})
            </h3>

            {/* Add Comment Form */}
            <form className="mb-8" onSubmit={handleSubmitForm1(commentPost)}>
              <div className="flex items-start gap-5">
                <div className="flex-1">
                  <Controller
                    name="desc"
                    control={controlForm1}
                    rules={{
                      required: {
                        value: true,
                        message: "Comment is required",
                      },
                      minLength: {
                        value: 2,
                        message: "Minimum number of characters is 2",
                      },
                      maxLength: {
                        value: 500,
                        message: "Maximum number of characters is 500",
                      },
                    }}
                    render={({ field }) => (
                      <Textarea
                        {...field}
                        onInput={(e) => setDescLength(e.target.value.length)}
                        variant="bordered"
                        maxLength={500}
                        placeholder="Write a comment..."
                        labelPlacement="outside"
                        classNames={{
                          base: "input-noMargin flex-1 h-[80px]",
                          inputWrapper: `border-[0.86px] p-0 border-solid ${
                            errorsForm1.desc?.message
                              ? "invalid-text"
                              : "border-[#E5E4EB]"
                          } rounded-[20px] min-h-[80px]`,
                        }}
                        isInvalid={errorsForm1.desc?.message ? true : false}
                        errorMessage={errorsForm1.desc?.message}
                      />
                    )}
                  />
                  {!errorsForm1.desc?.message && 500 - descLength > 0 ? (
                    <p className="text-[.75rem] text-mainBlue mt-1">
                      {500 - descLength} remaining characters
                    </p>
                  ) : null}
                </div>
                <button
                  type="submit"
                  className="bg-mainGradiant rounded-full w-[40px] h-[40px] flex justify-center items-center gap-2 ms-auto mt-2"
                >
                  <i className="text-white fa-solid fa-comment fa-lg"></i>
                </button>
              </div>
            </form>

            {/* Comments List */}
            <div className="space-y-4 ">
              {commentsList?.length > 0 ? (
                commentsList.map((item) => (
                  <div
                    key={item?.id}
                    className="flex justify-between rounded-[10px] overflow-hidden"
                  >
                    <div className="p-2.5 flex flex-col overflow-hidden flex-1">
                      <div className="flex items-start gap-2.5 mb-2">
                        <div
                          className={`min-w-[35px] min-h-[35px] max-w-[35px] max-h-[35px] rounded-full`}
                          style={{
                            backgroundimg: `url(${item?.user?.img_url})`,
                            backgroundRepeat: "no-repeat",
                            backgroundSize: "100%",
                            objectFit: "cover",
                            backgroundPosition: "50%",
                          }}
                        ></div>
                        <div className="flex bg-socialIconBg w-full justify-between items-start px-3 py-2 rounded-[20px]">
                          <div className="flex-col gap-[3px] flex-1">
                            <p className="text-base font-bold text-textColor">
                              {item?.user?.name}
                            </p>
                            <p className="mb-2 text-sm text-cardGrayColor">
                              {timeAgo(item?.updated_at)}
                            </p>
                            
                            {/* Inline Edit Form */}
                            {editingCommentId === item?.id ? (
                              <div className="mt-2">
                                <Textarea
                                  value={editingCommentText}
                                  onValueChange={(value) => {
                                    setEditingCommentText(value);
                                    setEditCommentLength(value.length);
                                  }}
                                  variant="bordered"
                                  maxLength={500}
                                  placeholder="Edit your comment..."
                                  labelPlacement="outside"
                                  classNames={{
                                    base: "input-noMargin flex-1 h-[60px]",
                                    inputWrapper: `border-[0.86px] p-0 border-solid border-[#E5E4EB] rounded-[20px] min-h-[60px]`,
                                  }}
                                />
                                <div className="flex items-center gap-2 mt-2">
                                  {editCommentLength > 0 && (
                                    <p className="text-[.75rem] text-mainBlue">
                                      {500 - editCommentLength} remaining characters
                                    </p>
                                  )}
                                  <button
                                    onClick={editComment}
                                    className="px-3 py-1 text-xs font-medium text-white rounded-full bg-mainGradiant"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={cancelEditComment}
                                    className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-300 rounded-full"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <p className="text-textColor leading-[22px] break-before-all">
                                {item?.comment}
                              </p>
                            )}
                          </div>
                          {isMyComment(item) && editingCommentId !== item?.id && (
                            <Dropdown
                              classNames={{
                                content:
                                  "min-w-[88px] max-w-[88px] py-0 px-[5px] rounded-[10px]",
                              }}
                              placement="left-start"
                            >
                              <DropdownTrigger>
                                <button
                                  className="w-[24px] h-[24px]  mt-0.5"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                  }}
                                >
                                  <img
                                    src={dots}
                                    alt="Dots"
                                    className="min-w-[24px]  min-h-[24px]"
                                  />
                                </button>
                              </DropdownTrigger>
                              <DropdownMenu
                                aria-label="Post Actions"
                                onAction={(key) => {
                                  if (key === "edit comment") {
                                    startEditComment(item);
                                  } else if (key === "delete comment") {
                                    if (window.confirm("Are you sure you want to delete this comment?")) {
                                      setTargetId(item?.id);
                                      deleteComment(item?.id, false, null);
                                    }
                                  }
                                }}
                              >
                                <DropdownItem
                                  textValue="edit comment"
                                  key="edit comment"
                                  className="text-textColor text-sm font-normal py-[2px] px-[5px] relative after:absolute after:left-0 after:right-0 after:bottom-[-2.5px] after:h-px after:bg-grayBorder mb-[5px] selected-action"
                                >
                                  Edit
                                </DropdownItem>
                                <DropdownItem
                                  key="delete comment"
                                  className="text-[#EB6270] text-sm font-normal py-[2px] px-[5px] selected-action"
                                  color="danger"
                                >
                                  Delete
                                </DropdownItem>
                              </DropdownMenu>
                            </Dropdown>
                          )}
                        </div>
                      </div>
                      <div
                        className={`flex items-center gap-5 ms-[55px] ${
                          item?.replies?.length > 0 && commentId !== item?.id
                            ? !showReplies.includes(item.id) && "mb-5"
                            : ""
                        }`}
                      >
                        <button
                          onClick={() => {
                            likeComment(item?.id);
                          }}
                          className={`flex items-center justify-start gap-[5px] transition-transform active:scale-95 ${
                            likedComments[item?.id] || item?.is_liked ? "text-[#E65CA7]" : "text-gray-500"
                          }`}
                        >
<img
                            src={(likedComments[item?.id] || item?.is_liked) ? commentLikeIcon : commentUnlikeIcon}
                            alt="Like"
                            className="w-[18px] h-[18px]"
                          />
                          {/* Always show like count, even if 0 */}
                          <h4
                            className={`text-sm font-normal leading-[18.4px] ${
                              likedComments[item?.id] || item?.is_liked
                                ? "text-[#E65CA7]"
                                : "text-textColor"
                            }`}
                          >
                            {item?.like_count ?? 0}
                          </h4>
                        </button>
                        <button
                          onClick={() => {
                            if (commentId) {
                              if (commentId === item?.id) {
                                setCommentId(null);
                                resetForm2();
                              } else {
                                setCommentId(null);
                                resetForm2();
                                setCommentId(item?.id);
                              }
                            } else {
                              setCommentId(item?.id);
                            }
                          }}
                          className={`text-sm text-textColor w-fit`}
                        >
                          Reply
                        </button>
                        {item?.replies?.length > 0 && (
                          <button
                            onClick={() => toggleReplies(item.id)}
                            className={`text-sm text-textColor w-fit`}
                          >
                            {!showReplies.includes(item.id)
                              ? `Show ${item?.replies?.length} ${
                                  item.replies?.length > 1 ? "Replies" : "Reply"
                                }`
                              : `Hide ${item?.replies?.length} ${
                                  item.replies?.length > 1 ? "Replies" : "Reply"
                                }`}
                          </button>
                        )}
                      </div>
                      {commentId === item?.id && (
                        <form
                          className="ms-[55px]"
                          onSubmit={handleSubmitForm2(replyComment)}
                        >
                          <div className="flex items-start gap-5 my-5">
                            <div className="flex-1">
                              <Controller
                                name="desc2"
                                control={controlForm2}
                                rules={{
                                  required: {
                                    value: true,
                                    message: "Reply is required",
                                  },
                                  minLength: {
                                    value: 2,
                                    message:
                                      "Minimum number of characters is 2",
                                  },
                                  maxLength: {
                                    value: 500,
                                    message:
                                      "Maximum number of characters is 500",
                                  },
                                }}
                                render={({ field }) => (
                                  <Textarea
                                    {...field}
                                    onInput={(e) =>
                                      setReplyDescLength(e.target.value.length)
                                    }
                                    variant="bordered"
                                    maxLength={500}
                                    placeholder="Write a reply..."
                                    labelPlacement="outside"
                                    classNames={{
                                      base: "input-noMargin flex-1 h-[80px]",
                                      inputWrapper: `border-[0.86px] p-0 border-solid ${
                                        errorsForm2.desc2?.message
                                          ? "invalid-text"
                                          : "border-[#E5E4EB]"
                                      } rounded-[20px] min-h-[80px]`,
                                    }}
                                    isInvalid={
                                      errorsForm2.desc2?.message ? true : false
                                    }
                                    errorMessage={errorsForm2.desc2?.message}
                                  />
                                )}
                              />
                              {!errorsForm2.desc2?.message &&
                              500 - replyDescLength > 0 ? (
                                <p className="text-[.75rem] text-mainBlue mt-1">
                                  {500 - replyDescLength} remaining characters
                                </p>
                              ) : null}
                            </div>
                            <button
                              type="submit"
                              className="bg-mainGradiant rounded-full w-[40px] h-[40px] flex justify-center items-center gap-2 ms-auto mt-2"
                            >
                              <i className="text-white fa-solid fa-comment fa-lg"></i>
                            </button>
                          </div>
                        </form>
                      )}

                      {/* Render Replies */}
                      {item?.replies?.length > 0 && showReplies.includes(item.id) && (
                        <div className="ms-[55px] mt-4 space-y-3">
                          {item.replies.map((reply) => (
                            <div
                              key={reply?.id}
                              className="flex items-start gap-2.5 bg-gray-50 p-3 rounded-[15px] relative"
                            >
                              <div
                                className="min-w-[28px] min-h-[28px] max-w-[28px] max-h-[28px] rounded-full bg-mainGradiant flex items-center justify-center overflow-hidden"
                                style={{
                                  backgroundImage: reply?.user_data?.img_url || reply?.user?.img_url 
                                    ? `url(${reply?.user_data?.img_url || reply?.user?.img_url})`
                                    : 'none',
                                  backgroundRepeat: "no-repeat",
                                  backgroundSize: "cover",
                                  objectFit: "cover",
                                  backgroundPosition: "50%",
                                }}
                              >
                                {!(reply?.user_data?.img_url || reply?.user?.img_url) && (
                                  <span className="text-xs font-bold text-white">
                                    {(reply?.user_data?.name || reply?.user?.name || "U").charAt(0)}
                                  </span>
                                )}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <p className="text-sm font-semibold text-textColor">
                                    {reply?.user_data?.name || reply?.user?.name || "User"}
                                  </p>
                                  <p className="text-xs text-cardGrayColor">
                                    {timeAgo(reply?.created_at || reply?.updated_at)}
                                  </p>
                                </div>
                                
                                {/* Reply Inline Edit Form */}
                                {editingCommentId === reply?.id ? (
                                  <div className="mt-2">
                                    <Textarea
                                      value={editingCommentText}
                                      onValueChange={(value) => {
                                        setEditingCommentText(value);
                                        setEditCommentLength(value.length);
                                      }}
                                      variant="bordered"
                                      maxLength={500}
                                      placeholder="Edit your reply..."
                                      labelPlacement="outside"
                                      classNames={{
                                        base: "input-noMargin flex-1 h-[60px]",
                                        inputWrapper: `border-[0.86px] p-0 border-solid border-[#E5E4EB] rounded-[20px] min-h-[60px]`,
                                      }}
                                    />
                                    <div className="flex items-center gap-2 mt-2">
                                      {editCommentLength > 0 && (
                                        <p className="text-[.75rem] text-mainBlue">
                                          {500 - editCommentLength} remaining characters
                                        </p>
                                      )}
                                      <button
                                        onClick={editComment}
                                        className="px-3 py-1 text-xs font-medium text-white rounded-full bg-mainGradiant"
                                      >
                                        Save
                                      </button>
                                      <button
                                        onClick={cancelEditComment}
                                        className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-300 rounded-full"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <p className="text-sm leading-relaxed text-textColor">
                                    {reply?.comment}
                                  </p>
                                )}
                              </div>
                              
                              {/* Reply Actions Dropdown */}
                        
                              {user && (
                                (reply?.user?.id === user.id || 
                                reply?.user_data?.id === user.id || 
                                reply?.user_id === user.id) && 
                                editingCommentId !== reply?.id && (
                                  <Dropdown
                                    classNames={{
                                      content: "min-w-[88px] max-w-[88px] py-0 px-[5px] rounded-[10px]",
                                    }}
                                    placement="left-start"
                                  >
                                    <DropdownTrigger>
                                      <button
                                        className="w-[20px] h-[20px] mt-0.5 absolute top-2 right-2"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                        }}
                                      >
                                        <img
                                          src={dots}
                                          alt="Dots"
                                          className="min-w-[20px] min-h-[20px]"
                                        />
                                      </button>
                                    </DropdownTrigger>
                                    <DropdownMenu
                                      aria-label="Reply Actions"
                                      onAction={(key) => {
                                        if (key === "edit reply") {
                                          startEditComment(reply);
                                        } else if (key === "delete reply") {
                                          if (window.confirm("Are you sure you want to delete this reply?")) {
                                            setTargetId(reply?.id);
                                            deleteComment(reply?.id, true, item?.id);
                                          }
                                        }
                                      }}
                                    >
                                      <DropdownItem
                                        textValue="edit reply"
                                        key="edit reply"
                                        className="text-textColor text-sm font-normal py-[2px] px-[5px] relative after:absolute after:left-0 after:right-0 after:bottom-[-2.5px] after:h-px after:bg-grayBorder mb-[5px] selected-action"
                                      >
                                        Edit
                                      </DropdownItem>
                                      <DropdownItem
                                        key="delete reply"
                                        className="text-[#EB6270] text-sm font-normal py-[2px] px-[5px] selected-action"
                                        color="danger"
                                      >
                                        Delete
                                      </DropdownItem>
                                    </DropdownMenu>
                                  </Dropdown>
                                )
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="py-8 text-center text-gray-500">
                  No comments yet. Be the first to share your thoughts!
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Share Modal Toast */}
      {shareModal && (
        <div className="fixed z-50 px-6 py-3 text-white bg-green-500 shadow-lg top-24 right-8 rounded-xl animate-bounce">
          Link copied to clipboard!
        </div>
      )}
    </section>
  );
};

export default BlogPost;